import * as XLSX from 'xlsx';

const excelExport = {
  // Generic export function
  exportToExcel: (data, filename, sheetName = 'Sheet1') => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
      throw new Error('Failed to export Excel file');
    }
  },

  // Export Daily Sales Report
  exportDailySales: (reportData) => {
    try {
      if (!reportData?.daily_sales) {
        throw new Error('No daily sales data available');
      }
      
      const data = reportData.daily_sales.map(item => ({
        'Date': item.date,
        'POS Sales': item.pos_sales || 0,
        'Ecommerce Sales': item.ecommerce_sales || 0,
        'Total Sales': item.total_sales || 0,
        'POS Transactions': item.pos_transactions || 0,
        'Ecommerce Transactions': item.ecommerce_transactions || 0,
        'Total Transactions': item.total_transactions || 0,
        'Average Transaction Value': item.average_transaction_value || 0
      }));
      excelExport.exportToExcel(data, 'Daily_Sales_Report');
    } catch (error) {
      console.error('Daily sales export error:', error);
      throw error;
    }
  },

  // Export Product Performance
  exportProductPerformance: (reportData) => {
    try {
      const posData = reportData.top_products_pos?.map(item => ({
        'Channel': 'POS',
        'Product Name': item.name || 'N/A',
        'SKU': item.sku || 'N/A',
        'Quantity Sold': item.total_quantity || 0,
        'Total Revenue': item.total_revenue || 0,
        'Total Cost': item.total_cost || 0,
        'Total Profit': item.total_profit || 0,
        'Profit Margin %': item.profit_margin || 0
      })) || [];

      const ecommerceData = reportData.top_products_ecommerce?.map(item => ({
        'Channel': 'Ecommerce',
        'Product Name': item.name || 'N/A',
        'SKU': item.sku || 'N/A',
        'Quantity Sold': item.total_quantity || 0,
        'Total Revenue': item.total_revenue || 0,
        'Total Cost': item.total_cost || 0,
        'Total Profit': item.total_profit || 0
      })) || [];

      const data = [...posData, ...ecommerceData];
      
      if (data.length === 0) {
        throw new Error('No product performance data available');
      }
      
      excelExport.exportToExcel(data, 'Product_Performance_Report');
    } catch (error) {
      console.error('Product performance export error:', error);
      throw error;
    }
  },

  // Export Inventory Report
  exportInventory: (reportData) => {
    try {
      if (!reportData?.products) {
        throw new Error('No inventory data available');
      }
      
      const data = reportData.products.map(item => ({
        'Product Name': item.name || 'N/A',
        'SKU': item.sku || 'N/A',
        'Current Stock': item.current_stock || 0,
        'Low Stock Threshold': item.low_stock_threshold || 0,
        'Stock Value': item.stock_value || 0,
        'Sold Last 30 Days': item.sales_data?.sold_last_30_days || 0,
        'Daily Sales Rate': item.sales_data?.daily_sales_rate || 0,
        'Days of Stock Remaining': item.sales_data?.days_of_stock_remaining || 0,
        'Stock Status': item.sales_data?.stock_status || 'N/A',
        'Needs Reorder': item.needs_reorder ? 'Yes' : 'No'
      }));
      excelExport.exportToExcel(data, 'Inventory_Report');
    } catch (error) {
      console.error('Inventory export error:', error);
      throw error;
    }
  },

  // Export Low Stock Alerts
  exportLowStockAlerts: (reportData) => {
    try {
      const lowStockData = reportData.low_stock_products?.map(item => ({
        'Alert Type': 'Low Stock',
        'Product Name': item.name || 'N/A',
        'SKU': item.sku || 'N/A',
        'Brand': item.brand || 'N/A',
        'Current Stock': item.current_stock || 0,
        'Low Stock Threshold': item.low_stock_threshold || 0,
        'Stock Value': item.stock_value || 0,
        'Urgency': item.urgency || 'N/A',
        'Category': item.category || 'N/A'
      })) || [];

      const outOfStockData = reportData.out_of_stock_products?.map(item => ({
        'Alert Type': 'Out of Stock',
        'Product Name': item.name || 'N/A',
        'SKU': item.sku || 'N/A',
        'Current Stock': item.current_stock || 0,
        'Category': item.category || 'N/A'
      })) || [];

      const data = [...lowStockData, ...outOfStockData];
      
      if (data.length === 0) {
        throw new Error('No low stock data available');
      }
      
      excelExport.exportToExcel(data, 'Low_Stock_Alerts');
    } catch (error) {
      console.error('Low stock alerts export error:', error);
      throw error;
    }
  },

  // Export Net Profit Report
  exportNetProfit: (reportData) => {
    try {
      const data = [{
        'Period': `${reportData.period?.start_date || 'N/A'} to ${reportData.period?.end_date || 'N/A'}`,
        'Total Revenue': reportData.profit_breakdown?.total_revenue || 0,
        'Total Cost': reportData.profit_breakdown?.total_cost || 0,
        'Gross Profit': reportData.profit_breakdown?.gross_profit || 0,
        'Gross Profit Margin %': reportData.profit_breakdown?.gross_profit_margin || 0,
        'Net Profit': reportData.profit_breakdown?.net_profit || 0,
        'Net Profit Margin %': reportData.profit_breakdown?.net_profit_margin || 0,
        'POS Revenue': reportData.pos_breakdown?.revenue || 0,
        'Ecommerce Revenue': reportData.ecommerce_breakdown?.revenue || 0
      }];
      excelExport.exportToExcel(data, 'Net_Profit_Report');
    } catch (error) {
      console.error('Net profit export error:', error);
      throw error;
    }
  },

  // Export Pending Ecommerce Orders
  exportPendingEcommerceOrders: (reportData) => {
    try {
      if (!reportData?.pending_orders) {
        throw new Error('No pending orders data available');
      }
      
      const data = reportData.pending_orders.map(item => ({
        'Order Number': item.order_number || 'N/A',
        'Status': item.status || 'N/A',
        'Customer Name': item.customer_name || 'N/A',
        'Customer Phone': item.customer_phone || 'N/A',
        'Total Amount': item.total_amount || 0,
        'Payment Method': item.payment_method || 'N/A',
        'Created At': item.created_at || 'N/A',
        'Items Count': item.items_count || 0,
        'Delivery Address': item.delivery_address || 'N/A',
        'City': item.city || 'N/A',
        'Time in Status (Hours)': item.time_in_status || 0
      }));
      excelExport.exportToExcel(data, 'Pending_Ecommerce_Orders');
    } catch (error) {
      console.error('Pending orders export error:', error);
      throw error;
    }
  },

  // Export Stock Valuation
  exportStockValuation: (reportData) => {
    try {
      const overallData = [{
        'Type': 'Overall Summary',
        'Total Products': reportData.overall_valuation?.total_products || 0,
        'Total Quantity': reportData.overall_valuation?.total_quantity || 0,
        'Total Cost Value': reportData.overall_valuation?.total_cost_value || 0,
        'Total Retail Value': reportData.overall_valuation?.total_retail_value || 0,
        'Potential Profit': reportData.overall_valuation?.potential_profit || 0,
        'Profit Margin %': reportData.overall_valuation?.profit_margin_percentage || 0
      }];

      const categoryData = reportData.valuation_by_category?.map(item => ({
        'Type': 'Category Breakdown',
        'Category Name': item.category_name || 'N/A',
        'Product Count': item.product_count || 0,
        'Total Quantity': item.total_quantity || 0,
        'Total Cost Value': item.total_cost_value || 0,
        'Total Retail Value': item.total_retail_value || 0
      })) || [];

      const topProductsData = reportData.top_valued_products?.map(item => ({
        'Type': 'Top Valued Product',
        'Product Name': item.name || 'N/A',
        'SKU': item.sku || 'N/A',
        'Stock Quantity': item.stock_quantity || 0,
        'Cost Price': item.cost_price || 0,
        'Retail Price': item.price || 0,
        'Cost Value': item.cost_value || 0,
        'Retail Value': item.retail_value || 0
      })) || [];

      const data = [...overallData, ...categoryData, ...topProductsData];
      
      if (data.length === 0) {
        throw new Error('No stock valuation data available');
      }
      
      excelExport.exportToExcel(data, 'Stock_Valuation_Report');
    } catch (error) {
      console.error('Stock valuation export error:', error);
      throw error;
    }
  },

  // Export Payment Methods
  exportPaymentMethods: (reportData) => {
    try {
      const posData = reportData.pos_payments?.map(payment => ({
        'Channel': 'POS',
        'Payment Method': payment.payment_method || 'N/A',
        'Transaction Count': payment.transaction_count || 0,
        'Total Amount': payment.total_amount || 0,
        'Average Transaction': payment.average_transaction || 0,
        'Percentage': payment.percentage || 0
      })) || [];

      const ecommerceData = reportData.ecommerce_payments?.map(payment => ({
        'Channel': 'Ecommerce',
        'Payment Method': payment.payment_method || 'N/A',
        'Transaction Count': payment.transaction_count || 0,
        'Total Amount': payment.total_amount || 0,
        'Average Transaction': payment.average_transaction || 0,
        'Percentage': payment.percentage || 0
      })) || [];

      const data = [...posData, ...ecommerceData];
      
      if (data.length === 0) {
        throw new Error('No payment methods data available');
      }
      
      excelExport.exportToExcel(data, 'Payment_Methods_Report');
    } catch (error) {
      console.error('Payment methods export error:', error);
      throw error;
    }
  },

  // Export Sales Comparison
  exportSalesComparison: (reportData) => {
    try {
      if (!reportData?.comparison_data) {
        throw new Error('No sales comparison data available');
      }
      
      const data = reportData.comparison_data.map(item => ({
        'Date': item.date || 'N/A',
        'POS Orders': item.pos?.order_count || 0,
        'POS Revenue': item.pos?.revenue || 0,
        'POS Average Order Value': item.pos?.average_order_value || 0,
        'Ecommerce Orders': item.ecommerce?.order_count || 0,
        'Ecommerce Revenue': item.ecommerce?.revenue || 0,
        'Ecommerce Average Order Value': item.ecommerce?.average_order_value || 0,
        'Total Revenue': item.total_revenue || 0
      }));
      excelExport.exportToExcel(data, 'Sales_Comparison_Report');
    } catch (error) {
      console.error('Sales comparison export error:', error);
      throw error;
    }
  },

  // Export Peak Sales Hours
  exportPeakSalesHours: (reportData) => {
    try {
      if (!reportData?.peak_hours) {
        throw new Error('No peak sales hours data available');
      }
      
      const data = reportData.peak_hours.map(item => ({
        'Hour': item.formatted_hour || 'N/A',
        'Total Orders': item.total_orders || 0,
        'Total Revenue': item.total_revenue || 0,
        'Average Order Value': item.average_order_value || 0
      }));
      excelExport.exportToExcel(data, 'Peak_Sales_Hours_Report');
    } catch (error) {
      console.error('Peak sales hours export error:', error);
      throw error;
    }
  },

  // Export Monthly Performance
  exportMonthlyPerformance: (reportData) => {
    try {
      if (!reportData?.monthly_comparison) {
        throw new Error('No monthly performance data available');
      }
      
      const data = reportData.monthly_comparison.map(item => ({
        'Month': item.month_name || 'N/A',
        'POS Orders': item.pos?.order_count || 0,
        'POS Revenue': item.pos?.total_revenue || 0,
        'POS Average Order Value': item.pos?.average_order_value || 0,
        'Ecommerce Orders': item.ecommerce?.order_count || 0,
        'Ecommerce Revenue': item.ecommerce?.total_revenue || 0,
        'Ecommerce Average Order Value': item.ecommerce?.average_order_value || 0,
        'Total Orders': item.combined?.order_count || 0,
        'Total Revenue': item.combined?.total_revenue || 0
      }));
      excelExport.exportToExcel(data, 'Monthly_Performance_Report');
    } catch (error) {
      console.error('Monthly performance export error:', error);
      throw error;
    }
  }
};

export default excelExport;